import React from 'react'
import { makeNoise } from './Ridge'
import { buildTiles, tilesNear, percentFound } from './fieldGeometry'

/*
 * ─── MosaicField ───
 * The Ask empty state's ground: the brand's triangles as dust across the top of the page.
 * They start as faint specks; moving the cursor over them opens a patch in ember tones, a
 * number in the corner counts how much of the field is found, and at 100 % the whole field lights, holds a
 * moment, and covers itself again. Fills its (relative) parent; listens to the window's
 * pointer, so the content above it stays clickable.
 */

const SIZE = 30
const REACH = 52          // px around the cursor that uncover
const HOLD_MS = 1100      // how long a completed field stays lit
const TOP = 0.46          // the field is the top of the page; below it is plain

const rgbOf = () => {
  const v = getComputedStyle(document.documentElement).getPropertyValue('--trodo-primary-rgb').trim()
  return (v || '224 122 69').split(/[ ,]+/).slice(0, 3).join(',')
}
const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark'

export default function MosaicField({ className = '' }) {
  const canvasRef = React.useRef(null)
  const [pct, setPct] = React.useState(0)

  React.useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas && canvas.parentElement
    if (!canvas || !host) return undefined
    const ctx = canvas.getContext('2d')
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const tone = makeNoise(11)
    const tone2 = makeNoise(52)

    let tiles = []
    let tone_ = []
    let found = []
    let level = []   // what is painted, eases toward what is found
    let glow = []    // a fresh find pulses, then settles
    let count = 0
    let inb = []     // tiles whose centre is on screen: the ones that count
    let total = 0
    let raf = 0
    let holdUntil = 0
    let resetTimer = 0
    let w = 0
    let h = 0
    let fh = 0
    let lastPct = 0

    const layout = () => {
      const r = host.getBoundingClientRect()
      const nw = Math.max(1, Math.round(r.width))
      const nh = Math.max(1, Math.round(r.height))
      if (tiles.length && Math.abs(nw - w) < 4 && Math.abs(nh - h) < 4) return
      const keep = new Set(tiles.filter((_, i) => found[i]).map((t) => `${t.x},${t.y}`))   // a resize keeps what is found
      w = nw
      h = nh
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      fh = h * TOP
      tiles = buildTiles(w, fh, SIZE)
      inb = tiles.map((t) => t.cx >= 0 && t.cx <= w && t.cy >= 0 && t.cy <= fh)
      total = inb.filter(Boolean).length
      tone_ = tiles.map((t) => {
        const v = tone(t.x * 0.42 + t.y * 0.19) * 0.65 + tone2(t.y * 0.57 - t.x * 0.13) * 0.35   // patches, like the Mosaic
        return 0.1 + 0.9 * Math.pow(v, 1.7)
      })
      found = tiles.map((t) => keep.has(`${t.x},${t.y}`))
      level = found.map((f) => (f ? 1 : 0))
      glow = tiles.map(() => 0)
      count = found.filter((f, i) => f && inb[i]).length
      lastPct = -1
      update()
      schedule()
    }

    const schedule = () => { if (!raf) raf = requestAnimationFrame(frame) }

    const frame = (now) => {
      raf = 0
      const rgb = rgbOf()
      const dark = isDark()
      const restA = dark ? 0.75 : 0.8
      const dustA = dark ? 0.16 : 0.2
      const lit = now < holdUntil
      let moving = lit
      ctx.clearRect(0, 0, w, h)
      for (let i = 0; i < tiles.length; i++) {
        const target = found[i] ? 1 : 0
        const speed = found[i] ? 0.22 : 0.05
        if (Math.abs(level[i] - target) > 0.004) { level[i] += (target - level[i]) * speed; moving = true } else level[i] = target
        if (glow[i] > 0.01) { glow[i] *= 0.93; moving = true } else glow[i] = 0
        const t = tiles[i]
        const fade = 1 - Math.min(1, Math.max(0, (t.cy / fh - 0.5) / 0.5))   // dust thins out toward the bottom of the field
        if (fade <= 0.01) continue
        // each tile is a speck: small and faint until found, then it opens up and lights
        const sc = 0.22 + 0.14 * level[i] + 0.3 * glow[i] + (lit ? 0.08 : 0)
        const a = fade * (dustA + level[i] * (restA * tone_[i] + 0.55 * glow[i] + (lit ? 0.25 : 0)))
        const p = t.pts
        ctx.beginPath()
        for (let k = 0; k < 3; k++) {
          const x = t.cx + (p[k * 2] - t.cx) * sc
          const y = t.cy + (p[k * 2 + 1] - t.cy) * sc
          if (k) ctx.lineTo(x, y); else ctx.moveTo(x, y)
        }
        ctx.closePath()
        ctx.fillStyle = `rgba(${rgb},${a.toFixed(3)})`
        ctx.fill()
      }
      if (moving) schedule()
    }

    const update = () => {
      const p = percentFound(count, total)
      if (p !== lastPct) { lastPct = p; setPct(p) }
    }

    const onMove = (e) => {
      if (reduce || holdUntil) return
      const r = host.getBoundingClientRect()
      const x = e.clientX - r.left
      const y = e.clientY - r.top
      if (x < 0 || y < 0 || x > r.width || y > fh) return
      let any = false
      for (const i of tilesNear(tiles, x, y, REACH)) {
        if (!found[i]) { found[i] = true; glow[i] = 1; if (inb[i]) count++; any = true }
      }
      if (!any) return
      update()
      if (count >= total) {
        holdUntil = performance.now() + HOLD_MS
        found = found.map(() => true)
        glow = glow.map(() => 0.6)
        resetTimer = setTimeout(() => {      // the field covers itself again
          holdUntil = 0
          found = found.map(() => false)
          count = 0
          update()
          schedule()
        }, HOLD_MS)
      }
      schedule()
    }

    layout()
    const ro = new ResizeObserver(layout)
    ro.observe(host)
    window.addEventListener('pointermove', onMove)
    const themeObs = new MutationObserver(schedule)
    themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(resetTimer)
      ro.disconnect()
      themeObs.disconnect()
      window.removeEventListener('pointermove', onMove)
    }
  }, [])

  return (
    <>
      <canvas ref={canvasRef} aria-hidden className={`absolute inset-0 h-full w-full pointer-events-none ${className}`} />
      <div
        aria-hidden
        className="absolute bottom-4 right-5 select-none text-[11px] tabular-nums pointer-events-none"
        style={{ color: 'var(--text-muted)', letterSpacing: '0.04em', opacity: 0.7 }}
      >
        {pct}%
      </div>
    </>
  )
}
